import * as z from 'zod';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Filter } from '@ais-ui/websockets';
import type { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { timeFormatHoursMinutesSecondsSchema, checkDirectionAngleSchema} from '@ais-ui/utils';
import { mutualDetectorFormSchema } from '../Mutations/detectorForm';

export const fieldToLineAccordionMap: {
  [key in keyof LineDetectorFormValues]: string;
} = {
  name: 'detector-item',
  enabled: 'detector-item',
  selectedGroups: 'detector-item',
  severity: 'detector-item',
  filterId: 'detector-item',
  maintainShiplist: 'detector-item',
  acknowledgeRequired: 'detector-item',
  timeBeforeActivation: 'advanced-item',
  timeBeforeDeactivation: 'advanced-item',
  timeBetweenTrigger: 'advanced-item',
  timeoutWhenActive: 'advanced-item',
  sendShipNotification: 'advanced-item',
  shipNotificationText: 'advanced-item',
  useVesselDimensions: 'advanced-item',
  navigationStatusTriggerThreshold: 'advanced-item',
  navigationStatusIgnoreThreshold: 'advanced-item',
  driftingSpeedOverGroundThreshold: 'advanced-item',
  driftingAngleThreshold: 'advanced-item',
  driftingTimeThreshold: 'advanced-item',
  exitCheckThreshold: 'advanced-item',
  exitCheckTriggerLost: 'advanced-item',
  deltaSpeedOverGroundThreshold: 'advanced-item',
  emails: 'detector-item',
  phones: 'detector-item',
};

export const lineDetectorFormSchema = mutualDetectorFormSchema
  .extend({
    activationText: z.string().optional(),
    deactivationText: z.string().optional(),
    directionMinAngle: checkDirectionAngleSchema.optional(),
    directionMaxAngle: checkDirectionAngleSchema.optional(),
    timeBeforeActivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBeforeDeactivation: timeFormatHoursMinutesSecondsSchema.optional(),
    timeBetweenTrigger: timeFormatHoursMinutesSecondsSchema.optional(),
    timeoutWhenActive: timeFormatHoursMinutesSecondsSchema.optional(),
    sendShipNotification: z.boolean(),
    shipNotificationText: z.string().optional(),
    navigationStatusTriggerThreshold: z.number().optional(),
    navigationStatusIgnoreThreshold: z.number().optional(),
    driftingSpeedOverGroundThreshold: z.number().optional(),
    driftingAngleThreshold: z.number().optional(),
    driftingTimeThreshold: z.number().optional(),
    exitCheckThreshold: z.number().optional(),
    exitCheckTriggerLost: z.boolean().optional(),
    deltaSpeedOverGroundThreshold: z.number().optional(),
    useVesselDimensions: z.boolean(),
  })
  .refine((data) => Boolean(data.shapeId) || Boolean(data.shape), {
    message: 'Shape must be provided',
    path: ['shapeId', 'shape'],
  });
export type LineDetectorFormValues = z.infer<typeof lineDetectorFormSchema>;

export const convertFormValuesToBackend = (
  detectorType: string,
  data: LineDetectorFormValues,
  shape: GeoJson,
  filter: Filter | undefined
): EventdetectorDefinition => {
  let checkDirection = null;
  if (data.directionMinAngle && data.directionMaxAngle) {
    checkDirection = {
      minAngle: data.directionMinAngle,
      maxAngle: data.directionMaxAngle,
    };
  }

  const notifications = [];
  for (const email of data.emails) {
    notifications.push({
      who: email,
      action: 'email',
    });
  }

  for (const phone of data.phones) {
    notifications.push({
      who: phone,
      action: 'sms',
    });
  }

  return {
    name: data.name,
    type: detectorType,
    acl: data.selectedGroups.map((group) => group.value),
    id: null,
    enabled: data.enabled,
    advancedConfiguration: true,
    checkDirection: checkDirection,
    useVesselDimensions: data.useVesselDimensions as boolean,
    entireVessel: false,
    sendShipNotification: data.sendShipNotification as boolean,
    shipNotificationText: data.shipNotificationText,
    timeBeforeActivation: data.timeBeforeActivation
      ? data.timeBeforeActivation
      : '',
    timeBeforeDeactivation: data.timeBeforeDeactivation
      ? data.timeBeforeDeactivation
      : '',
    activationText: data.activationText ? data.activationText : '',
    deactivationText: data.deactivationText ? data.deactivationText : '',
    timeBetweenTriggers: data.timeBetweenTrigger ? data.timeBetweenTrigger : '',
    timeoutWhenActive: data.timeoutWhenActive ? data.timeoutWhenActive : '',
    severity: data.severity,
    shape: data.shapeId,
    geometry: shape,
    filterId: filter?.uuid ? filter.uuid : null,
    filterString: filter?.filter ? filter?.filter : '',
    notify: notifications,
    notifyOnActivation: true,
    notifyOnDeactivation: true,
    ackRequired: data.acknowledgeRequired,
    shiplist: {
      active: data.maintainShiplist,
    },
  };
};
